/**
 * Who an intake request is about.
 *
 * Normally a scheduler radiologist (`radId`). But the Drive is the wider source
 * of truth — a rad can have an intake folder while onboarding without ever
 * having a scheduler record — so a subject can instead be addressed by its Drive
 * folder name (`radKey`, `"Last, First"`). Every per-rad call in `api/intake.ts`
 * takes a subject rather than a bare id.
 *
 * Exactly one of the two is set: the union makes "neither" and "both" impossible
 * to construct, so no request can go out carrying an empty `rad_key`, and no
 * caller has to guess which field takes precedence. Use `subjectOf` to make that
 * choice from a roster row.
 */
export type IntakeSubject =
  | { radId: number; radKey?: undefined }
  | { radId?: undefined; radKey: string };

/** A subject for a radiologist that has a scheduler record. */
export function dbSubject(radId: number): IntakeSubject {
  return { radId };
}

/** A subject for a radiologist known only by their Drive folder. */
export function driveSubject(radKey: string): IntakeSubject {
  return { radKey };
}

/**
 * The one identity a selectable radiologist is addressed by.
 *
 * A roster row can carry *both* — a radiologist with a scheduler record also has
 * a Drive folder — so the choice has to be made explicitly, once, rather than
 * left to whichever field each helper happens to check first. The scheduler
 * record wins: it survives a rename, whereas the folder name is the identity and
 * changes with it. `null` for a row carrying neither, which nothing can address.
 */
export function subjectOf(rad: {
  rad_id: number | null;
  rad_key: string | null;
}): IntakeSubject | null {
  if (rad.rad_id != null) return dbSubject(rad.rad_id);
  if (rad.rad_key) return driveSubject(rad.rad_key);
  return null;
}

/**
 * Stable string identity — react-query cache keys, and the `<Select>` value.
 * The `db:`/`drive:` prefixes keep a numeric id from ever colliding with a
 * folder name.
 */
export function subjectKey(subject: IntakeSubject): string {
  return subject.radId != null ? `db:${subject.radId}` : `drive:${subject.radKey}`;
}

/** Parse a `subjectKey` back into a subject (the selector's onChange). */
export function subjectFromKey(key: string): IntakeSubject | null {
  if (key.startsWith("db:")) {
    const id = Number(key.slice(3));
    return Number.isFinite(id) ? dbSubject(id) : null;
  }
  if (key.startsWith("drive:")) {
    const radKey = key.slice(6);
    return radKey ? driveSubject(radKey) : null;
  }
  return null;
}

export function sameSubject(a: IntakeSubject | null, b: IntakeSubject | null): boolean {
  if (a == null || b == null) return a === b;
  return subjectKey(a) === subjectKey(b);
}

/** Query-string params identifying the subject. */
export function subjectParams(subject: IntakeSubject): Record<string, string> {
  return subject.radId != null ? { rad_id: String(subject.radId) } : { rad_key: subject.radKey };
}

/** JSON body fields identifying the subject. */
export function subjectBody(subject: IntakeSubject): { rad_id?: number; rad_key?: string } {
  return subject.radId != null ? { rad_id: subject.radId } : { rad_key: subject.radKey };
}

/** Append the subject to a multipart form. */
export function appendSubject(form: FormData, subject: IntakeSubject): void {
  if (subject.radId != null) form.append("rad_id", String(subject.radId));
  else form.append("rad_key", subject.radKey);
}
